/**
 * Prompt Library System Types
 *
 * This module defines the data structures for the prompt library feature,
 * including user prompts, organization prompts, and system templates.
 */

import { z } from 'zod';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('lib:types');

// ============================================================================
// ENUMS & CONSTANTS
// ============================================================================

export const PROMPT_CATEGORIES = [
  'prior-auth',
  'coding-billing',
  'error-detection',
  'patient-communication',
  'medical-records',
  'eligibility',
  'claims-processing',
  'general',
] as const;

export type PromptCategory = (typeof PROMPT_CATEGORIES)[number];

export const PROMPT_CATEGORY_LABELS: Record<PromptCategory, string> = {
  'prior-auth': 'Prior Authorization',
  'coding-billing': 'Coding & Billing',
  'error-detection': 'Error Detection',
  'patient-communication': 'Patient Communication',
  'medical-records': 'Medical Records',
  eligibility: 'Eligibility',
  'claims-processing': 'Claims Processing',
  general: 'General',
};

export const PROMPT_CATEGORY_ICONS: Record<PromptCategory, string> = {
  'prior-auth': 'ClipboardList',
  'coding-billing': 'DollarSign',
  'error-detection': 'AlertCircle',
  'patient-communication': 'MessageSquare',
  'medical-records': 'FileText',
  eligibility: 'CheckCircle',
  'claims-processing': 'BarChart3',
  general: 'Zap',
};

export type PromptSource = 'user' | 'org' | 'system';

// ============================================================================
// ZOD SCHEMAS
// ============================================================================

export const PromptVariableSchema = z.object({
  name: z.string().min(1), // e.g., "patient_name"
  label: z.string().min(1), // e.g., "Patient Name"
  placeholder: z.string().optional(), // e.g., "John Doe"
  required: z.boolean().optional().default(false),
});

export const PromptTemplateSchema = z.object({
  id: z.string(),
  title: z.string().min(1).max(200),
  description: z.string().max(500).optional(),
  content: z.string().min(1).max(10000), // The actual prompt text
  category: z.enum(PROMPT_CATEGORIES),
  variables: z.array(PromptVariableSchema).optional().default([]),
  source: z.enum(['user', 'org', 'system']),
  createdBy: z.string().optional(), // userId for user/org prompts
  createdAt: z.number(),
  updatedAt: z.number(),
  // NOTE: usageCount and isFavorite are tracked separately in client store, not in prompt object
});

// Dynamic suggestions (AI-generated)
export const DynamicSuggestionSchema = z.object({
  id: z.string(),
  label: z.string().min(1).max(100), // Short title for chip
  prompt: z.string().min(1).max(2000), // Full prompt text
  confidence: z.number().min(0).max(1), // 0-1, how relevant
  reasoning: z.string().optional(), // Why AI suggested this
});

// Suggestion cache
export const SuggestionCacheSchema = z.object({
  contextHash: z.string(),
  suggestions: z.array(DynamicSuggestionSchema),
  timestamp: z.number(),
  screenCaptureId: z.string().optional(),
});

// ============================================================================
// API INPUT/OUTPUT SCHEMAS
// ============================================================================

export const CreatePromptInputSchema = PromptTemplateSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  source: true,
});

export const UpdatePromptInputSchema = PromptTemplateSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  source: true,
}).partial();

export const GetOrgPromptsOutputSchema = z.object({
  prompts: z.array(PromptTemplateSchema),
  total: z.number(),
});

export const CreatePromptOutputSchema = z.object({
  prompt: PromptTemplateSchema,
  message: z.string(),
});

export const UpdatePromptOutputSchema = z.object({
  prompt: PromptTemplateSchema,
  message: z.string(),
});

export const DeletePromptOutputSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

// ============================================================================
// USER PREFERENCES (for cross-device sync)
// ============================================================================

export const UserPromptPreferencesSchema = z.object({
  favoriteIds: z.array(z.string()).default([]),
  recentlyUsedIds: z.array(z.string()).default([]),
  usageCounts: z.record(z.string(), z.number()).default({}),
  lastSyncedAt: z.number().optional(),
});

export const GetUserPreferencesOutputSchema = z.object({
  preferences: UserPromptPreferencesSchema,
});

export const UpdateUserPreferencesInputSchema = UserPromptPreferencesSchema.partial().omit({
  lastSyncedAt: true,
});

export const UpdateUserPreferencesOutputSchema = z.object({
  preferences: UserPromptPreferencesSchema,
  message: z.string(),
});

// ============================================================================
// AI SUGGESTIONS - INPUT/OUTPUT SCHEMAS
// ============================================================================

export const GenerateSuggestionsInputSchema = z.object({
  screenCaptureId: z.string().optional().describe('ID of the current screen capture to analyze'),
  recentMessages: z
    .array(z.string())
    .optional()
    .describe('Recent chat messages for context (last 3-5 messages)'),
  additionalContext: z
    .string()
    .optional()
    .describe('Any additional context to help generate relevant suggestions'),
});

export const GenerateSuggestionsOutputSchema = z.object({
  suggestions: z.array(DynamicSuggestionSchema),
  contextAnalysis: z
    .string()
    .optional()
    .describe('Brief explanation of the context that informed the suggestions'),
});

// ============================================================================
// TYPESCRIPT TYPES
// ============================================================================

export type PromptVariable = z.infer<typeof PromptVariableSchema>;
export type PromptTemplate = z.infer<typeof PromptTemplateSchema>;
export type DynamicSuggestion = z.infer<typeof DynamicSuggestionSchema>;
export type SuggestionCache = z.infer<typeof SuggestionCacheSchema>;
export type CreatePromptInput = z.infer<typeof CreatePromptInputSchema>;
export type UpdatePromptInput = z.infer<typeof UpdatePromptInputSchema>;
export type GetOrgPromptsOutput = z.infer<typeof GetOrgPromptsOutputSchema>;
export type CreatePromptOutput = z.infer<typeof CreatePromptOutputSchema>;
export type UpdatePromptOutput = z.infer<typeof UpdatePromptOutputSchema>;
export type DeletePromptOutput = z.infer<typeof DeletePromptOutputSchema>;
export type UserPromptPreferences = z.infer<typeof UserPromptPreferencesSchema>;
export type GetUserPreferencesOutput = z.infer<typeof GetUserPreferencesOutputSchema>;
export type UpdateUserPreferencesInput = z.infer<typeof UpdateUserPreferencesInputSchema>;
export type UpdateUserPreferencesOutput = z.infer<typeof UpdateUserPreferencesOutputSchema>;
export type GenerateSuggestionsInput = z.infer<typeof GenerateSuggestionsInputSchema>;
export type GenerateSuggestionsOutput = z.infer<typeof GenerateSuggestionsOutputSchema>;

// ============================================================================
// WORKOS METADATA TYPES
// ============================================================================

/**
 * WorkOS User Metadata Structure
 *
 * Defines the structure of data stored in WorkOS user.metadata field.
 * This ensures type safety when reading/writing to WorkOS on both frontend and backend.
 *
 * Usage:
 * - Backend: Cast WorkOS metadata to this type when reading
 * - Backend: Use this type when writing to WorkOS
 * - Frontend: Use this type when parsing API responses
 */
export type WorkOSUserMetadata = {
  /** User-created prompt templates */
  prompts?: PromptTemplate[];
  /** User preferences for prompt library (favorites, recently used, usage counts) */
  prompt_preferences?: UserPromptPreferences;
  /** Allow other metadata fields that we don't manage */
  [key: string]: unknown;
};

/**
 * Helper to safely parse WorkOS metadata
 * Returns a typed object with defaults if fields are missing
 * Handles JSON.parse for stringified complex objects
 */
export const parseWorkOSUserMetadata = (metadata: unknown): WorkOSUserMetadata => {
  if (!metadata || typeof metadata !== 'object') {
    return {};
  }

  const meta = metadata as Record<string, unknown>;
  const parsed: WorkOSUserMetadata = {};

  // Parse prompts field
  if (meta.prompts) {
    try {
      const promptsData =
        typeof meta.prompts === 'string' ? JSON.parse(meta.prompts) : meta.prompts;
      if (Array.isArray(promptsData)) {
        parsed.prompts = promptsData as PromptTemplate[];
      }
      // Don't set to undefined - just omit the property
    } catch (error) {
      logger.error('[parseWorkOSUserMetadata] Failed to parse prompts:', error);
      // Don't set to undefined - just omit the property
    }
  }

  // Parse prompt_preferences field
  if (meta.prompt_preferences) {
    try {
      const prefsData =
        typeof meta.prompt_preferences === 'string'
          ? JSON.parse(meta.prompt_preferences)
          : meta.prompt_preferences;
      if (prefsData && typeof prefsData === 'object') {
        parsed.prompt_preferences = prefsData as UserPromptPreferences;
      }
      // Don't set to undefined - just omit the property
    } catch (error) {
      logger.error('[parseWorkOSUserMetadata] Failed to parse prompt_preferences:', error);
      // Don't set to undefined - just omit the property
    }
  }

  // Preserve other fields
  for (const [key, value] of Object.entries(meta)) {
    if (key !== 'prompts' && key !== 'prompt_preferences') {
      parsed[key] = value;
    }
  }

  return parsed;
};

/**
 * Helper to serialize data for WorkOS metadata
 * Ensures we're sending the correct structure
 * WorkOS accepts Record<string, string | null> so we JSON.stringify complex objects
 */
export const serializeWorkOSUserMetadata = (
  updates: Partial<WorkOSUserMetadata>
): Record<string, string | null> => {
  const serialized: Record<string, string | null> = {};

  for (const [key, value] of Object.entries(updates)) {
    if (value === undefined) {
      continue; // Skip undefined values
    } else if (value === null) {
      serialized[key] = null;
    } else if (typeof value === 'string') {
      serialized[key] = value;
    } else {
      // JSON.stringify complex objects (arrays, objects)
      serialized[key] = JSON.stringify(value);
    }
  }

  return serialized;
};

// ============================================================================
// SYSTEM TEMPLATES (Hardcoded Medical Prompts)
// ============================================================================

export const SYSTEM_PROMPT_TEMPLATES: PromptTemplate[] = [
  // Prior Authorization
  {
    id: 'sys-prior-auth-generate',
    title: 'Generate Prior Authorization',
    description: 'Create a prior authorization request from medical records',
    content:
      'Based on the medical information provided, generate a complete prior authorization request for {{procedure}}. Include medical necessity justification, relevant diagnosis codes, and supporting clinical documentation.',
    category: 'prior-auth',
    source: 'system',
    variables: [
      {
        name: 'procedure',
        label: 'Procedure/Treatment',
        placeholder: 'e.g., MRI of lumbar spine',
        required: true,
      },
    ],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  },
  {
    id: 'sys-prior-auth-medical-necessity',
    title: 'Summarize Medical Necessity',
    description: 'Extract and summarize medical necessity from records',
    content:
      'From the medical records shown, extract and summarize the medical necessity for the requested procedure/treatment. Format it appropriately for insurance submission.',
    category: 'prior-auth',
    source: 'system',
    variables: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  },
  {
    id: 'sys-prior-auth-appeal',
    title: 'Draft Prior Auth Appeal',
    description: 'Create an appeal letter for denied prior authorization',
    content:
      'Based on the denial reason and medical documentation, draft a compelling prior authorization appeal letter. Include clinical rationale, supporting evidence, and relevant medical literature or guidelines.',
    category: 'prior-auth',
    source: 'system',
    variables: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  },

  // Error Detection
  {
    id: 'sys-error-spot-check',
    title: 'Spot Check for Errors',
    description: 'Review claim or form for common errors',
    content:
      'Carefully review the attached claim/form and identify any errors, inconsistencies, or missing information. Check for: incorrect patient demographics, invalid CPT/ICD codes, missing signatures, date discrepancies, and billing errors.',
    category: 'error-detection',
    source: 'system',
    variables: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  },
  {
    id: 'sys-error-code-validation',
    title: 'Validate Medical Codes',
    description: 'Check if CPT and ICD codes are valid and properly matched',
    content:
      'Review the CPT and ICD-10 codes shown and verify: (1) codes are valid and current, (2) diagnosis codes support the procedures, (3) no conflicting or mutually exclusive codes, (4) modifiers are appropriate.',
    category: 'error-detection',
    source: 'system',
    variables: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  },

  // Eligibility
  {
    id: 'sys-eligibility-check',
    title: 'Check Patient Eligibility',
    description: 'Verify insurance eligibility and coverage',
    content:
      'Based on the patient information shown, help me verify eligibility for {{service}}. Identify what information I need to check, common coverage limitations, and potential authorization requirements.',
    category: 'eligibility',
    source: 'system',
    variables: [
      {
        name: 'service',
        label: 'Service/Procedure',
        placeholder: 'e.g., MRI, Physical Therapy',
        required: false,
      },
    ],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  },
  {
    id: 'sys-eligibility-benefits',
    title: 'Explain Benefits Coverage',
    description: 'Interpret insurance benefits and coverage details',
    content:
      'Review the insurance benefits information shown and explain in clear terms: what is covered, any limitations or exclusions, patient cost-sharing (deductible, copay, coinsurance), and any special requirements.',
    category: 'eligibility',
    source: 'system',
    variables: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  },

  // Coding & Billing
  {
    id: 'sys-coding-suggest-cpt',
    title: 'Suggest CPT Codes',
    description: 'Recommend appropriate CPT codes for procedures',
    content:
      'Based on the procedure documentation shown, suggest the most appropriate CPT code(s). Include any necessary modifiers and explain the rationale for each code selection.',
    category: 'coding-billing',
    source: 'system',
    variables: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  },
  {
    id: 'sys-coding-suggest-icd',
    title: 'Suggest ICD-10 Codes',
    description: 'Recommend appropriate diagnosis codes',
    content:
      'Based on the clinical documentation shown, suggest appropriate ICD-10 diagnosis codes. Ensure codes are specific, support medical necessity, and follow proper sequencing guidelines.',
    category: 'coding-billing',
    source: 'system',
    variables: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  },
  {
    id: 'sys-coding-bundle-check',
    title: 'Check for Bundled Services',
    description: 'Identify services that may be bundled or unbundled',
    content:
      'Review the CPT codes shown and identify any services that may be subject to bundling rules (CCI edits). Highlight codes that cannot be billed together and suggest appropriate alternatives or modifiers.',
    category: 'coding-billing',
    source: 'system',
    variables: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  },

  // Claims Processing
  {
    id: 'sys-claims-denial-analysis',
    title: 'Analyze Claim Denial',
    description: 'Review denial reason and suggest next steps',
    content:
      'Analyze the claim denial shown and provide: (1) explanation of the denial reason, (2) whether it can be corrected and resubmitted or requires appeal, (3) specific steps to resolve, (4) additional documentation needed.',
    category: 'claims-processing',
    source: 'system',
    variables: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  },
  {
    id: 'sys-claims-readiness-check',
    title: 'Claim Submission Readiness',
    description: 'Verify claim is ready for submission',
    content:
      'Review the claim and verify it is ready for submission. Check for: complete patient demographics, valid insurance information, appropriate codes, supporting documentation, proper authorization numbers, and compliance with payer requirements.',
    category: 'claims-processing',
    source: 'system',
    variables: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  },

  // Medical Records
  {
    id: 'sys-records-summarize',
    title: 'Summarize Medical Records',
    description: 'Create concise summary of medical documentation',
    content:
      'Summarize the medical records shown in a clear, organized format. Include: chief complaint, relevant history, clinical findings, diagnosis, treatment plan, and any outstanding issues or follow-up needed.',
    category: 'medical-records',
    source: 'system',
    variables: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  },
  {
    id: 'sys-records-extract-info',
    title: 'Extract Specific Information',
    description: 'Pull specific data points from medical records',
    content:
      'From the medical records shown, extract the following information: {{information_needed}}. Provide the data in a structured, easy-to-reference format.',
    category: 'medical-records',
    source: 'system',
    variables: [
      {
        name: 'information_needed',
        label: 'Information Needed',
        placeholder: 'e.g., all lab results, medication list, surgery dates',
        required: true,
      },
    ],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  },

  // Patient Communication
  {
    id: 'sys-patient-explain-benefits',
    title: 'Explain to Patient',
    description: 'Translate medical/insurance info for patient understanding',
    content:
      'Explain the information shown in simple, patient-friendly language. Avoid medical jargon and focus on what the patient needs to know and do next.',
    category: 'patient-communication',
    source: 'system',
    variables: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  },
  {
    id: 'sys-patient-cost-estimate',
    title: 'Estimate Patient Cost',
    description: 'Calculate patient financial responsibility',
    content:
      "Based on the insurance benefits and procedure information shown, estimate the patient's out-of-pocket cost. Break down: deductible remaining, copay/coinsurance amounts, and total estimated patient responsibility.",
    category: 'patient-communication',
    source: 'system',
    variables: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  },

  // General
  {
    id: 'sys-general-explain',
    title: "Explain What I'm Looking At",
    description: 'General explanation of screen content',
    content:
      'Explain what is shown on the screen. Identify the type of document or form, key information present, and any notable items that require attention.',
    category: 'general',
    source: 'system',
    variables: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  },
  {
    id: 'sys-general-next-steps',
    title: 'What Should I Do Next?',
    description: 'Suggest next actions based on context',
    content:
      'Based on what is shown, suggest the appropriate next steps. Identify any missing information, required actions, or potential issues that need to be addressed.',
    category: 'general',
    source: 'system',
    variables: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  },
];

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get system templates by category
 */
export function getSystemTemplatesByCategory(category: PromptCategory): PromptTemplate[] {
  return SYSTEM_PROMPT_TEMPLATES.filter(t => t.category === category);
}

/**
 * Get all categories with their template counts
 */
export function getCategoriesWithCounts(
  templates: PromptTemplate[]
): Array<{ category: PromptCategory; count: number }> {
  const counts = new Map<PromptCategory, number>();

  templates.forEach(template => {
    counts.set(template.category, (counts.get(template.category) || 0) + 1);
  });

  return PROMPT_CATEGORIES.map(category => ({
    category,
    count: counts.get(category) || 0,
  }));
}

/**
 * Replace variables in prompt content
 * Example: "Check {{patient_name}}" with { patient_name: "John" } => "Check John"
 */
export function replacePromptVariables(content: string, values: Record<string, string>): string {
  let result = content;

  Object.entries(values).forEach(([key, value]) => {
    const pattern = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
    result = result.replace(pattern, value);
  });

  return result;
}

/**
 * Extract variable names from prompt content
 * Example: "Check {{patient_name}} for {{procedure}}" => ["patient_name", "procedure"]
 */
export function extractVariableNames(content: string): string[] {
  // Fallback for invalid input
  if (!content || typeof content !== 'string') {
    return [];
  }

  try {
    const pattern = /{{(\s*[\w-]+\s*)}}/g;
    const matches = [...content.matchAll(pattern)];

    // Extract and deduplicate variable names
    const varNames = matches
      .map(match => match[1]?.trim())
      .filter((name): name is string => Boolean(name));

    // Return unique variable names only
    return [...new Set(varNames)];
  } catch (error) {
    logger.error('Error extracting variable names:', error);
    return [];
  }
}

/**
 * Validate that all required variables have values
 */
export function validatePromptVariables(
  template: PromptTemplate,
  values: Record<string, string>
): { valid: boolean; missingVariables: string[] } {
  const requiredVars = template.variables?.filter(v => v.required).map(v => v.name) || [];

  const missingVariables = requiredVars.filter(
    varName => !values[varName] || values[varName].trim() === ''
  );

  return {
    valid: missingVariables.length === 0,
    missingVariables,
  };
}
